import * as React from 'react';
import toast from 'react-hot-toast';
import { API_GetFeatures, ApiResponse, BaseFeaturesModel } from '../apis/Apis';
import { PropsWithChildren, useEffect, useState } from 'react';
import { UserResponseModel, LoginResponseModel, LoginMFAResponseModel, API_VerifyLogin, API_LogOut, API_Login, API_LoginMFA } from '../apis/ApisAuth';
import { UserType, LogonStatus } from '../application-form/Variables';
import { API_GetPositionOverview, PositionsModel } from '../apis/ApisPositions';
import { API_EmailLogs, EmailLogContainerModel } from '../apis/ApisEmailLog';
import { API_ConfrimPrivacyPolicy } from '../apis/ApisPrivacyPolicyUpdate';
import { API_CheckVerificationEmail } from '../apis/ApiEmailVerification';

export type AppContextProviderProps = {
    apiRoot: string,
    appRoot: string,
    systemRoot: string,
    firmName: string,
    redesign_Carousel: boolean,
    redesign_BannerOnly: boolean,
    redesign_BannerVer: string,
    styleColourPrimary: string,
    styleColourSecondary: string,
    styleColourAlternative: string,
    styleColourAllHires: string,
    positionsLabel: string,
}

export type AppState = {
    loading: boolean,
    apiRoot: string,
    appRoot: string,
    systemRoot: string,
    firmName: string,
    redesign_Carousel: boolean,
    redesign_BannerOnly: boolean,
    redesign_BannerVer: string,
    styleColourPrimary: string,
    styleColourSecondary: string,
    styleColourAlternative: string,
    styleColourAllHires: string,
    positionsLabel: string,
    token?: string | null,
    userLoggedIn: boolean,
    loggedInUser: UserResponseModel,
    positions: PositionsModel,
    baseFeatures: BaseFeaturesModel,
    emailLogs: EmailLogContainerModel,
    policyUpdateToDate: boolean;
    logInUser: (username: string, password: string) => Promise<ApiResponse<LoginResponseModel>>;
    logInUserMFA: (username: string, code: string, mfaType: string) => Promise<ApiResponse<LoginMFAResponseModel>>;
    logOutUser: () => void;
    verifyUser: (showAlert: boolean) => void;
    verifyPolicyRequired: () => void;
    verifyEmailAuthenticated: () => Promise<boolean>;
}

export const AppContext = React.createContext<AppState>(null);


export const AppContextProvider: React.FC<PropsWithChildren<AppContextProviderProps>> = (props) => {

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const paramValue = urlParams.get('token');
    const [token] = useState<string | null>(paramValue);

    const [loggedInUserData, setLoggedInUserData] = useState<UserResponseModel>(null);
    const [userLoggedIn, setUserLoggedIn] = useState<boolean>(false);
    const [loading, loadingSet] = useState<boolean>(true);
    const [positions, setPositions] = useState<PositionsModel>(null);
    const [features, setFeatures] = useState<BaseFeaturesModel>(null);
    const [emailLogs, setEmailLogs] = useState<EmailLogContainerModel>(null);
    const [policyUpdateToDate, setPolicyUpdateToDate] = useState<boolean>(false);

    useEffect(() => {
        async function asyncCall() {
            try {
                loadingSet(true);
                await verifyUser();

                var positions = await API_GetPositionOverview(token, false);
                setPositions(positions);

                var features = await API_GetFeatures(token);
                setFeatures(features);
            } catch (error) {

            } finally {
                loadingSet(false);
            }
        }
        asyncCall();
    }, []);

    useEffect(() => {
        if (userLoggedIn && loggedInUserData.UserType == UserType.Candidate)
            loadEmailLogData();

    }, [userLoggedIn]);

    const loadEmailLogData = async () => {
        if (userLoggedIn && loggedInUserData.UserType == UserType.Candidate) {
            var eml = await API_EmailLogs();
            setEmailLogs(eml);
        }
    }

    const verifyUser = async () => {
        const data = await API_VerifyLogin();
        setLoggedInUserData(data);

        if (data.UserType != UserType.Anonymous) {
            setUserLoggedIn(true);
        }
    }

    const verifyEmailAuthenticated = async () => {
        const result = await API_CheckVerificationEmail();
        if (result.EmailVerified) {
            setLoggedInUserData({ ...loggedInUserData, EmailAuthenticated: true });
        }
        return result.EmailVerified;
    }

    const verifyPolicyRequired = async () => {
        setPolicyUpdateToDate(true);
        const result = await API_ConfrimPrivacyPolicy();
        if (!result.Success) {
            setPolicyUpdateToDate(false);
        }
    }

    const logOutUser = async () => {
        await API_LogOut();

        const data = await API_VerifyLogin();
        setUserLoggedIn(false);
        setLoggedInUserData(data);
        toast.success("Logged out");
    }

    const logInUser = async (username: string, password: string) => {
        const res = await API_Login(username, password);
        if (res.Status == LogonStatus.PASS) {
            if (!res.MFAPrompt) {
                toast.success("Logged in")
                await verifyUser();

                var backgrops = document.getElementsByClassName("offcanvas-backdrop");
                if (backgrops != null && backgrops.length > 0)
                    backgrops[0].remove();
            }
            else {
                toast.success("Multi factor authentication required");
            }
        }
        return res;
    }

    const logInUserMFA = async (username: string, code: string, mfaType: string) => {
        const res = await API_LoginMFA(username, code, mfaType);

        if (res.LoginSuccess) {
            toast.success("Logged in");
            await verifyUser();

            var backgrops = document.getElementsByClassName("offcanvas-backdrop");
            if (backgrops != null && backgrops.length > 0)
                backgrops[0].remove();
        }

        return res;
    }

    const state: AppState = {
        loading: loading,
        apiRoot: props.apiRoot,
        appRoot: props.appRoot,
        systemRoot: props.systemRoot,
        firmName: props.firmName,
        redesign_Carousel: props.redesign_Carousel,
        redesign_BannerOnly: props.redesign_BannerOnly,
        redesign_BannerVer: props.redesign_BannerVer,
        styleColourPrimary: props.styleColourPrimary,
        styleColourSecondary: props.styleColourSecondary,
        styleColourAlternative: props.styleColourAlternative,
        styleColourAllHires: props.styleColourAllHires,
        positionsLabel: props.positionsLabel,
        token: token,
        userLoggedIn: userLoggedIn,
        loggedInUser: loggedInUserData,
        positions: positions,
        baseFeatures: features,
        emailLogs: emailLogs,
        policyUpdateToDate: policyUpdateToDate,
        logInUser,
        logInUserMFA,
        logOutUser,
        verifyUser,
        verifyPolicyRequired,
        verifyEmailAuthenticated
    }

    return (<AppContext.Provider value={state}>
        {props.children}
    </AppContext.Provider>);
}
